import {
  createBrowserRouter,
  Navigate,
  Outlet,
} from "react-router-dom";
import ErrorPage from "../../error-page";
import Layout from "./Layout";
import SavLayout from "./Sav/Layout";
import RequireAuth from "./Auth/RequireAuth";
import Login from "./Auth/Login";
import Documents from "./Sav/Pages/Documents";
import Theme from "./Theme/Theme";
import CurrentSavContainer from "./Sav/CurrentSavContainer";
import TasksList from "./TasksList";
import VoucherV2Flow from "./Sav/Pages/Reparator/Voucher/V2";
import AdminLayoutBPAR from "../../layout/AdminLayoutBPAR";
import IAConversationsListPage from "./Sav/Pages/IAConversationsListPage";
import IAConversationPage from "./Sav/Pages/IAConversationPage";
import NotFound from "./NotFound";
import RedirectOutilsPro from "./RedirectOutilsPro";
import RedirectOutilsProSav from "./RedirectOutilsProSav";

const routerSav = createBrowserRouter([
  {
    path: "/",
    errorElement: <ErrorPage />,
    element: <Theme />,
    children: [
      {
        path: '/garant/sav',
        element: <RedirectOutilsPro />
      },
      {
        path: '/garant/sav/:id',
        element: <RedirectOutilsProSav />
      },
      {
        path: "/login",
        element: <Login />,
      },
      {
        path: "/status/:status",
        element: <RequireAuth><Layout /></RequireAuth>,
        children: [
          {
            index: true,
            lazy: () => import("./SavList").then(m => ({ Component: m.default })),
          }
        ]
      },
      {
        // Route pour la nouvelle interface V2 avec AdminLayout sans sidebar
        path: "/sav/:savId/reparator/voucher/v2",
        element: (
          <RequireAuth>
            <CurrentSavContainer>
              <AdminLayoutBPAR>
                <VoucherV2Flow />
              </AdminLayoutBPAR>
            </CurrentSavContainer>
          </RequireAuth>
        ),
      },
      {
        path: "/sav-v2",
        element: <RequireAuth><Outlet /></RequireAuth>,
        children: [
          {
            path: ":id",
            lazy: () => import("./SavV2").then(m => ({ Component: m.default })),
          },
          {
            path: ":id/intervention/:interventionId",
            lazy: () => import("./SavV2").then(m => ({ Component: m.default })),
          },
          {
            // Preview pour les gestionnaires (sans token)
            path: ":id/preview-garant-validation",
            lazy: () => import("./GarantValidation").then(m => ({ Component: m.default })),
          }
        ]
      },
      {
        // Page publique pour les garants (avec token) - SANS authentification
        path: "/public-validation",
        lazy: () => import("./GarantValidation").then(m => ({ Component: m.default })),
      },
      {
        // Page de succès après validation - SANS authentification
        path: "/validation-success",
        lazy: () => import("./ValidationSuccess").then(m => ({ Component: m.default })),
      },
      {
        path: "/",
        element: <RequireAuth><Layout /></RequireAuth>,
        children: [
          {
            index: true,
            lazy: () => import("./Dashboard").then(m => ({ Component: m.default })),
          },
          {
            path: "/list",
            lazy: () => import("./SavList").then(m => ({ Component: m.default })),
          },
          {
            path: "/all",
            lazy: () => import("./SavList").then(m => ({ Component: m.default })),
          },
          {
            path: "/savs-list-unified",
            lazy: () => import("./SavListUnified").then(m => ({ Component: m.default })),
          },
          {
            path: "/savs-v2",
            lazy: () => import("./SavListV2").then(m => ({ Component: m.default })),
          },
          {
            path: "/savs-v2/filter/:filterType",
            lazy: () => import("./SavListV2").then(m => ({ Component: m.default })),
          },
          {
            path: "/savs-v2/dashboard",
            lazy: () => import("./SavV2Dashboard").then(m => ({ Component: m.default })),
          },
          {
            path: "/contracts-list",
            lazy: () => import("./ContractsList").then(m => ({ Component: m.default })),
          },
          {
            // Nouveautés (fil d'annonces produit) : fil et page d'une nouveauté
            path: "/nouveautes",
            lazy: () => import("./NewsReader").then(m => ({ Component: m.NewsFeedRoute })),
          },
          {
            path: "/nouveautes/:slug",
            lazy: () => import("./NewsReader").then(m => ({ Component: m.NewsDetailRoute })),
          },
          {
            path: "/release-notes",
            lazy: () => import("./ReleaseNotes").then(m => ({ Component: m.default })),
          },
          {
            path: "/contract/:contractId",
            lazy: () => import("./ContractDetail").then(m => ({ Component: m.default })),
          },
          {
            path: "/tasks",
            element: <Navigate to="/tasks/calendar" replace />,
          },
          {
            path: "/tasks/calendar",
            element: <TasksList defaultTab="calendar" />,
          },
          {
            path: "/tasks/overdue",
            element: <TasksList defaultTab="overdue" />,
          },
          {
            path: "/tasks/upcoming",
            element: <TasksList defaultTab="upcoming" />,
          },
          {
            path: "/tasks/completed",
            element: <TasksList defaultTab="completed" />,
          },
          {
            path: "/sav/:savId",
            element: <CurrentSavContainer><SavLayout /></CurrentSavContainer>,
            children: [
              {
                index: true,
                lazy: () => import("./Sav/Pages/DashboardV2").then(m => ({ Component: m.default })),
              },
              {
                path: "dashboard-v1",
                lazy: () => import("./Sav/Pages/Dashboard").then(m => ({ Component: m.default })),
              },
              {
                path: "internal-documents",
                element: <Documents isInternal />,
              },
              {
                path: "documents",
                element: <Documents />,
              },
              {
                path: "beneficiary-messages",
                lazy: () => import("./Sav/Pages/Messages/WithBeneficiary").then(m => ({ Component: m.default })),
              },
              {
                path: "internal-messages",
                lazy: () => import("./Sav/Pages/Messages/InternalMessages").then(m => ({ Component: m.default })),
              },
              {
                path: "reparateur",
                lazy: () => import("./Sav/Pages/Reparator").then(m => ({ Component: m.default })),
              },
              {
                path: "tasks-calendar",
                lazy: () => import("./Sav/Pages/CalendarTaskList").then(m => ({ Component: m.default })),
              },
              {
                path: "actions-logs",
                lazy: () => import("./Sav/Pages/ActionsLogs").then(m => ({ Component: m.default })),
              },
              {
                path: "ia",
                children: [
                  {
                    index: true,
                    element: <CurrentSavContainer><IAConversationsListPage /></CurrentSavContainer>,
                  },
                  {
                    path: ":convId",
                    element: <CurrentSavContainer><IAConversationPage /></CurrentSavContainer>,
                  },
                ],
              },
            ]
          },

          {
            path: "/settings",
            lazy: () => import("./Settings/Layout").then(m => ({ Component: m.default })),
            children: [
              {
                path: "tags",
                lazy: () => import("./Settings/Pages/Tags").then(m => ({ Component: m.default })),
              }
            ]
          }
        ]
      },

    ],
  },

  {
    path: '*',
    element: <NotFound />,
  }

]);

export default routerSav;